/**
 * The music (GDD §9; judgment call 49): an original generative score in D minor at 104 BPM over an eight-bar
 * cycle. A pad always plays; the arpeggio, bass, hi-hats, kick, snare and a driving ostinato enter as the intensity
 * rises. Everything here is a pure function of the bar, the sixteenth-note step and the intensity, so the patterns
 * are unit-tested and the scheduler (src/audio/music.ts) allocates nothing.
 */
import { smoothstep } from './tuning.ts';

export const BPM = 104;
/** One sixteenth note (s). */
export const STEP_SECS = 60 / BPM / 4;
export const STEPS_PER_BAR = 16;
export const BARS = 8;
export const CYCLE_STEPS = STEPS_PER_BAR * BARS;

/** Triads in the pad's register (MIDI), one per bar: Dm, B♭, F, C, Gm, B♭, Dm, A. */
const CHORDS: readonly (readonly [number, number, number])[] = [
  [62, 65, 69],
  [62, 65, 70],
  [60, 65, 69],
  [60, 64, 67],
  [62, 67, 70],
  [62, 65, 70],
  [62, 65, 69],
  [61, 64, 69],
];
/** Bass roots (MIDI), one per bar. */
const ROOTS: readonly number[] = [38, 34, 41, 36, 43, 34, 38, 33];
/** The arpeggio walks the chord in eighth notes: chord tone index, +3 meaning the root an octave up. */
const ARP: readonly number[] = [0, 1, 2, 3, 2, 1, 0, 1];
/** Bass rhythm at low intensity (sixteenth steps of the bar): syncopated. */
const BASS_SPARSE: readonly number[] = [1, 0, 0, 1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 0, 1, 0];

/** Intensity at which each layer reaches half volume. */
export const LAYER_AT = {
  arp: 0.18,
  bass: 0.3,
  hats: 0.42,
  kick: 0.55,
  snare: 0.65,
  drive: 0.78,
} as const;
export type Layer = keyof typeof LAYER_AT;

/** A layer's volume (0–1) at an intensity: it fades in across ±0.08 around its threshold. */
export function layerLevel(intensity: number, layer: Layer): number {
  const at = LAYER_AT[layer];
  return smoothstep(at - 0.08, at + 0.08, intensity);
}

const wrapBar = (bar: number): number => ((bar % BARS) + BARS) % BARS;

/** Chord tone `i` (0–2) of a bar's triad, in the pad's register. */
export function chordNote(bar: number, i: number): number {
  const c = CHORDS[wrapBar(bar)] ?? CHORDS[0];
  return (c?.[i] ?? 62) as number;
}

export function bassRoot(bar: number): number {
  return ROOTS[wrapBar(bar)] ?? 38;
}

/** The arpeggio's note on an eighth-note step (0–15 sixteenths; odd steps rest), two octaves above the pad. */
export function arpNote(bar: number, step: number): number {
  if (step % 2) return -1;
  const k = ARP[(step >> 1) % ARP.length] ?? 0;
  return k === 3 ? chordNote(bar, 0) + 24 : chordNote(bar, k) + 12;
}

/** The bass note on a step, or −1 for a rest. Sparse and syncopated at first; driving eighths with octaves later. */
export function bassNote(bar: number, step: number, intensity: number): number {
  const root = bassRoot(bar);
  if (intensity < 0.6) return BASS_SPARSE[step] ? root : -1;
  if (step % 2) return -1;
  return step % 4 === 2 ? root + 12 : root;
}

/** Drum hits on a step as bits: 1 kick, 2 snare, 4 closed hat, 8 accented hat. */
export const KICK = 1;
export const SNARE = 2;
export const HAT = 4;
export const HAT_ACCENT = 8;

export function drums(step: number, intensity: number): number {
  let bits = 0;
  if (intensity >= LAYER_AT.kick - 0.08) {
    if (step === 0 || step === 8) bits |= KICK;
    if (intensity >= 0.75 && (step === 4 || step === 12)) bits |= KICK;
    if (intensity >= 0.85 && step === 10) bits |= KICK;
  }
  if (intensity >= LAYER_AT.snare - 0.08 && (step === 4 || step === 12)) bits |= SNARE;
  if (intensity >= LAYER_AT.hats - 0.08) {
    if (step % 2 === 0) bits |= step % 4 === 2 ? HAT_ACCENT : HAT;
    else if (intensity >= 0.7) bits |= HAT;
  }
  return bits;
}

/** The driving ostinato: sixteenths alternating the root and fifth, three octaves above the bass. */
export function driveNote(bar: number, step: number): number {
  return bassRoot(bar) + 36 + (step % 2 ? 7 : 0);
}
